import {DeprecatedDataFrame} from 'data-frames/DataFrameTypes';

/**
 * Check if the keys in an object are a subset of the keys of an other object
 *
 * Example usage 1:
 *
 *    areObjectKeysSubset({x: 1, y: 2}, {x: 1, y: 2, z: 2})
 *    > true
 *
 * Example usage 2:
 *
 *    areObjectKeysSubset({x: 1, y: 2, z: 2}, {x: 1, y: 2})
 *    > false
 *
 * Example usage 3:
 *
 *    areObjectKeysSubset({x: 1, y: 2}, {x: 1, y: 2})
 *    > true
 */
function areObjectKeysSubset(maybeSubsetObject: object, objectToCheck: object): boolean {
  return Object.keys(maybeSubsetObject).every((column) => Object.keys(objectToCheck).includes(column));
}

/**
 * Check if a dataFrame is compatible with the columns of another.
 *
 * In cases where a dataFrame's column's are unknown, we can use this function to check if the dataFrame has at
 * least the columns that the dataFrame we're comparing against has. We only check that we have at least the
 * target dataFrame's columns and not exactly the same columns because dataFrame functions are typically
 * implemented to only need at least certain columns instead of exactly certain columns. The check is done
 * both on the column definitions and the fields of the rows.
 *
 * Example usage 1:
 *
 *    areDataFrameColumnsCompatible(
 *      {
 *        axisTypes: {x: DataType.NUMBER, y: DataType.NUMBER, z: DataType.NUMBER},
 *        points: [...]
 *      },
 *      {
 *        axisTypes: {x: DataType.NUMBER, y: DataType.NUMBER},
 *        points: [...]
 *      },
 *    )
 *    > true
 *
 * Example usage 2:
 *
 *    areDataFrameColumnsCompatible(
 *      {
 *        axisTypes: {x: DataType.NUMBER z: DataType.NUMBER},
 *        points: [...]
 *      },
 *      {
 *        axisTypes: {x: DataType.NUMBER, y: DataType.NUMBER},
 *        points: [...]
 *      },
 *    )
 *    > false
 *
 * @deprecated This function is implemented with an old version of the DataFrame interface. New uses are discouraged,
 * instead the function should be converted to use the new interface.
 */
// TODO(BNCH-24546): Remove or reimplement data frame utils that use DeprecatedDataFrame
export default function areDataFrameColumnsCompatible<DataRow extends object>(
  unknownTypedDataFrame: DeprecatedDataFrame<object>,
  dataFrameToMatch: DeprecatedDataFrame<DataRow>
): unknownTypedDataFrame is DeprecatedDataFrame<DataRow> {
  return (
    areObjectKeysSubset(dataFrameToMatch.axisTypes, unknownTypedDataFrame.axisTypes) &&
    unknownTypedDataFrame.points.every((row) => areObjectKeysSubset(dataFrameToMatch.axisTypes, row))
  );
}
